import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { IEventTelemetry, IExceptionTelemetry } from "@microsoft/applicationinsights-web";

const useAppInsights = () => {
  const appInsights = useAppInsightsContext();
  /**
   * Logs a custom event to Application Insights.
   * @param eventName - The name of the event.
   * @param properties - Optional flat object containing event properties.
   */
  const logCustomEvent = (
    eventName: IEventTelemetry["name"],
    properties?: IEventTelemetry["properties"]
  ): void => {
    if (appInsights) {
      appInsights.trackEvent({ name: eventName, properties });
    }
  };
  /**
   * Logs an exception to Application Insights.
   *
   * @param error - The exception object to be logged.
   * @param properties - Optional additional properties to include with the exception telemetry.
   *
   * @remarks
   * This function checks if the Application Insights instance (`appInsights`) is available,
   * and if so, tracks the exception using the provided error and optional properties.
   */
  const logError = (
    error: IExceptionTelemetry["exception"],
    properties?: IExceptionTelemetry["properties"]
  ): void => {
    if (appInsights) {
      appInsights.trackException({ exception: error, properties });
    }
  };
  /**
   * Logs a page view to Application Insights.
   *
   * Tracks the current page view using the document title and current URL.
   * Useful for manual page view tracking in non-SSR React environments.
   *
   * @remarks
   * This function should be called after navigation events or when you want to
   * explicitly record a page view. It uses the browser's `document.title` and
   * `window.location.href` for the telemetry data.
   *
   * @throws {Error} If called in a non-browser environment (no window/document).
   *
   * @example
   * ```typescript
   * // Track a page view after navigation
   * logPageView();
   * ```
   *
   * @see {@link https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript#track-page-views}
   */
  const logPageView = (userId?: string, authProvider?: string): void => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      appInsights?.trackException({
        exception: new Error("logPageView called in non-browser environment (no window/document)"),
      });
      return;
    }
    if (appInsights) {
      appInsights.trackPageView({
        name: document.title,
        uri: window.location.href,
        properties: {
          userId,
          authProvider,
        },
      });
    }
  };
  return { appInsights, logCustomEvent, logError, logPageView };
};
export { useAppInsights };
